import { assert } from '../app/logger.js';
import { services } from '../app/services.js';
import { AsyncDataPipeableArray } from '../base/pipeable/async_data_pipeable_array.js';
import { PipeableTree } from '../base/pipeable/pipeable_tree.js';
import { ChecklistPipeables } from './checklist_pipeables.js';

const kDefaultItemName = 'Start typing here';

class Checklist extends AsyncDataPipeableArray {
  get id() {
    return this.#id;
  }

  get editableItems() {
    return this.#toTree(this.data.filter(i => !i.generated));
  }

  get foodMenuItems() {
    // The tree has a top level food menu item only.
    let items = [...this.data.filter(i => i.generated)];
    let foodMenuItemIdx = items.findIndex(
      item => item.generated && item.parentId == null
    );
    if (foodMenuItemIdx != -1) {
      items[foodMenuItemIdx] = Object.assign({}, items[foodMenuItemIdx], {
        idx: 0,
      });
    }
    return this.#toTree(items);
  }

  get packItems() {
    return this.#toTree(this.data);
  }

  updateEditableItems(itemTree) {
    let diff = PipeableTree.computeDiff(this.editableItems, itemTree);

    // If top level items are inserted or deleted, then make sure to shift
    // an auto generated top level food menu item to keep it as the last
    // item.
    let foodMenuRootItem = this.data.find(
      item => item.generated && item.parentId == null
    );
    if (foodMenuRootItem) {
      let foodMenuRootItemNewIdx = itemTree.children.length;
      if (foodMenuRootItem.idx != foodMenuRootItemNewIdx) {
        diff.unshift({
          id: foodMenuRootItem.id,
          idx: foodMenuRootItemNewIdx,
          what: 'shifted',
        });
      }
    }

    // Initalize item's state if needed.
    this.#setState(diff);

    return this.#update(diff);
  }

  updateFoodMenuItems(itemTree) {
    let diff = PipeableTree.computeDiff(this.foodMenuItems, itemTree);

    // Adjust kFoodChecklistItemTitle root item index in the checklist.
    let insertedRootItem = diff.find(
      entry => entry.what == 'inserted' && entry.parentId == null
    );
    if (insertedRootItem) {
      insertedRootItem.idx = this.packItems.children.length;
    }

    // Add 'generated' state to food menu checklist items.
    this.#setGeneratedState(diff);

    return this.#update(diff);
  }

  async packGear(list) {
    const entityId = await this.#getId();
    return services.trip.packGear(entityId, list);
  }

  async learn() {
    const entityId = await this.#getId();
    return services.trip.learn(entityId);
  }

  async getLoadPromise() {
    let entityId = await this.#getId();
    let items = await services.trip.getChecklist(entityId);

    // If no editable items, then insert a dummy one.
    if (!items || items.length == 0) {
      items = await services.trip.updateChecklist(entityId, [
        {
          what: 'inserted',
          name: kDefaultItemName,
          parentId: null,
          idx: 0,
          state: [],
          children: [],
        },
      ]);
    }
    return this.#mapItems(items);
  }

  get dataClassName() {
    return 'checklist';
  }

  constructor(trip, checklistIdGetter) {
    super();

    this.#trip = trip;
    this.#idGetter = checklistIdGetter;
  }

  async #update(diff) {
    let entityId = await this.#getId();
    let items = await services.trip.updateChecklist(entityId, diff);
    this.set(this.#mapItems(items));
  }

  #mapItems(items) {
    return items.map(item => ({
      id: item.id,
      parentId: item.parentId,
      name: item.name,
      idx: item.idx,
      state: item.state || [],
      checked: (item.state && item.state.includes('checked')) || false,
      generated: (item.state && item.state.includes('generated')) || false,
      toString() {
        return `${this.id}:'${this.name}'`;
      },
    }));
  }

  #toTree(items) {
    let root = { children: [] };

    if (items instanceof Array) {
      items = items.map(item => Object.assign({}, item));
      let byid = new Map([[null, root]]);
      for (let item of items) {
        item.children = [];
        byid.set(item.id, item);
      }

      for (let item of items) {
        let parentItem = byid.get(item.parentId);
        assert(
          parentItem,
          `[data] no parent item for ${item.id}:'${item.name}'`
        );
        assert(
          !(item.idx in parentItem.children),
          `[data] ${item.idx} index is overriden by ${item.id}:'${
            item.name
          }' in ${parentItem.id}:${
            parentItem.name
          }, children [${parentItem.children
            .map(child => `${child.id}:${child.name}`)
            .join(', ')}]`
        );
        item.parent = parentItem;
        if (parentItem) {
          parentItem.children[item.idx] = item;
        }
      }
    }

    return root;
  }

  #setGeneratedState(children) {
    for (let child of children) {
      if (!child.state) {
        child.state = [];
      }
      if (!child.state.includes('generated')) {
        child.state.push('generated');
      }
      if (child.children) {
        this.#setGeneratedState(child.children);
      }
    }
  }

  #setState(children) {
    for (let child of children) {
      if (!child.state) {
        child.state = (child.origData && child.origData.state) || [];
      }
      if (child.children) {
        this.#setState(child.children);
      }
    }
  }

  async #getId() {
    if (!this.#id) {
      this.#id = await this.#idGetter();
    }
    return this.#id;
  }

  static Sort = (a, b) => a.id - b.id;
  static Pipeables = ChecklistPipeables;

  #trip;
  #id;
  #idGetter;
}

export { Checklist };
